import React from 'react';

// Shared Reporting UI primitives — cards, the section header, and the refresh
// control that both Reporting tabs (Briefs + Suppliers) render, so their chrome
// stays consistent and lives in one place.

// Card surfaces. Two documented sizes: the Briefs tab uses the roomier `lg`
// surface; the Suppliers tab uses the tighter `sm` one.
export const CARD_SHELL = 'bg-white border border-line rounded-xl p-5 overflow-hidden';
export const CARD_SHELL_SM = 'bg-white border border-line rounded-lg p-4';

// Section header — muted-grey eyebrow (matches the app's heading direction) +
// a thin rule + a right-aligned descriptor. `withTopRule` adds top spacing and
// a divider to separate a major section from the one above.
export function SectionHeader({ label, sub, withTopRule = false }) {
  return (
    <div className={`flex items-center gap-4 mb-4 ${withTopRule ? 'mt-10 pt-8 border-t border-line' : 'mt-6'}`}>
      <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.25em] uppercase text-ink-secondary">{label}</p>
      <span className="flex-1 h-px bg-white" />
      {sub && <span className="font-['Metropolis'] text-[11px] text-[#51534A]">{sub}</span>}
    </div>
  );
}

// Refresh control — an "Updated …" stamp + a bordered refresh button that spins
// while `busy`. One look for both tabs' toolbars.
export function RefreshControl({ updatedLabel, onRefresh, busy = false }) {
  return (
    <div className="flex items-center gap-3">
      {updatedLabel && <span className="font-['Metropolis'] text-[11px] text-[#51534A]">{updatedLabel}</span>}
      <button
        type="button"
        onClick={onRefresh}
        disabled={busy}
        title="Refresh data"
        aria-label="Refresh reporting data"
        className="w-7 h-7 flex items-center justify-center bg-transparent border border-line rounded text-[#51534A] hover:text-active-green hover:border-[#DADCCD] transition-colors disabled:opacity-40">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={busy ? 'animate-spin' : ''}>
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      </button>
    </div>
  );
}

// Plain container card — for charts, lists and composites that own their own
// inner layout. A thin wrapper over the shared shell.
export function Card({ className = '', children, ...rest }) {
  return (
    <div className={`${CARD_SHELL} ${className}`} {...rest}>
      {children}
    </div>
  );
}

// Clickable / static stat card. When `onClick` is set it renders a
// keyboard-accessible `role="button"` div (NOT a <button>, so a rich subline
// can carry its own drill buttons without nesting interactives) — Enter/Space
// activate, hover dims. `label` renders the standard header row; `headerRight`
// fills an optional right-hand slot (e.g. a period dropdown). Omit `label` when
// the children already supply their own header.
export function StatCard({ label, headerRight, onClick, ariaLabel, className = '', children }) {
  const header = (label || headerRight) ? (
    <div className="flex items-center justify-between mb-3.5">
      {label ? <span className="fusion-field-label">{label}</span> : <span aria-hidden="true" />}
      {headerRight || null}
    </div>
  ) : null;

  if (!onClick) {
    return (
      <div className={`${CARD_SHELL} ${className}`}>
        {header}
        {children}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } }}
      className={`${CARD_SHELL} text-left cursor-pointer hover:opacity-85 transition-opacity ${className}`}>
      {header}
      {children}
    </div>
  );
}
